'use client';

import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Search, Upload, X, FileText, AlertCircle, CheckCircle, Loader2 } from '@/components/ui/icons';
import { DocumentRow, type Document as DocumentRowType } from './document-row';
import type { Document as APIDocument } from '@/hooks/use-artifacts';
import { useUploadDocument, useDeleteDocument, useDownloadDocument, useViewDocument } from '@/hooks/use-artifacts';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ACCEPTED_TYPES = '.pdf,.docx,.txt,.md,.markdown';
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
  'text/x-markdown',
  'application/octet-stream',
];

type UploadStatus = 'pending' | 'uploading' | 'done' | 'error';

interface QueueItem {
  id: string;
  file: File;
  status: UploadStatus;
  error?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getFileType(mimeType: string): string {
  if (mimeType.includes('pdf')) return 'pdf';
  if (mimeType.includes('word') || mimeType.includes('docx')) return 'docx';
  if (mimeType.includes('text')) return 'txt';
  if (mimeType.includes('image')) return 'image';
  return 'file';
}

function transformDocument(doc: APIDocument): DocumentRowType {
  return {
    id: doc.id,
    name: doc.filename,
    size: formatFileSize(doc.size),
    uploadedAt: new Date(doc.createdAt).toLocaleDateString(),
    uploadedBy: doc.uploadedBy?.name || doc.uploadedBy?.email || 'Unknown',
    status: doc.status.toLowerCase() as DocumentRowType['status'],
    chunks: doc.chunksCount || undefined,
    type: getFileType(doc.mimeType),
  };
}

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) {
    return `${file.name} exceeds the 50MB size limit (${formatFileSize(file.size)})`;
  }
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  const validExts = ACCEPTED_TYPES.split(',');
  if (!validExts.includes(ext) && !ACCEPTED_MIME_TYPES.includes(file.type)) {
    return `${file.name} is not a supported file type`;
  }
  return null;
}

interface KnowledgePanelProps {
  projectId?: string;
  projectName?: string;
  documents?: APIDocument[];
  onUpload?: () => void;
}

export function KnowledgePanel({ 
  projectId = '', 
  projectName = 'Project', 
  documents: apiDocuments = [],
  onUpload 
}: KnowledgePanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<QueueItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isProcessingRef = useRef(false);
  const { toast } = useToast();
  
  const uploadMutation = useUploadDocument(projectId);
  const deleteMutation = useDeleteDocument(projectId);
  const downloadDocument = useDownloadDocument(projectId);
  const viewDocument = useViewDocument(projectId);

  const documents = useMemo(() => 
    apiDocuments.map(transformDocument),
    [apiDocuments]
  );

  const stats = useMemo(() => {
    const totalSize = apiDocuments.reduce((acc, doc) => acc + doc.size, 0);
    const processed = apiDocuments.filter(doc => doc.status === 'PROCESSED' && (doc.chunksCount || 0) > 0).length;
    const totalChunks = apiDocuments.reduce((acc, doc) => acc + (doc.chunksCount || 0), 0);
    
    return {
      totalDocuments: apiDocuments.length,
      processed,
      totalSize: formatFileSize(totalSize),
      totalChunks,
    };
  }, [apiDocuments]);

  const filteredDocuments = documents.filter((doc) =>
    doc.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const hasActiveUploads = uploadQueue.some(q => q.status === 'pending' || q.status === 'uploading');
  const allDone = uploadQueue.length > 0 && !hasActiveUploads;

  useEffect(() => {
    if (!allDone) return;
    const timer = setTimeout(() => setUploadQueue([]), 3000);
    return () => clearTimeout(timer);
  }, [allDone]);

  const enqueueFiles = useCallback((files: File[]) => {
    const validItems: QueueItem[] = [];
    const errors: string[] = [];

    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validItems.push({
          id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          status: 'pending',
        });
      }
    }

    if (errors.length > 0) {
      toast({
        title: `${errors.length} file${errors.length > 1 ? 's' : ''} rejected`,
        description: errors.join('. '),
        variant: 'destructive',
      });
    }

    if (validItems.length > 0) {
      setUploadQueue(prev => [...prev, ...validItems]);
    }
  }, [toast]);

  useEffect(() => {
    if (isProcessingRef.current) return;

    const nextPending = uploadQueue.find(q => q.status === 'pending');
    if (!nextPending) return;

    isProcessingRef.current = true;

    setUploadQueue(prev =>
      prev.map(q => q.id === nextPending.id ? { ...q, status: 'uploading' as const } : q)
    );

    uploadMutation.mutateAsync(nextPending.file)
      .then(() => {
        setUploadQueue(prev =>
          prev.map(q => q.id === nextPending.id ? { ...q, status: 'done' as const } : q)
        );
      })
      .catch((err) => {
        setUploadQueue(prev =>
          prev.map(q => q.id === nextPending.id ? { ...q, status: 'error' as const, error: err.message } : q)
        );
      })
      .finally(() => {
        isProcessingRef.current = false;
      });
  }, [uploadQueue, uploadMutation]);

  useEffect(() => {
    if (!allDone) return;
    const succeeded = uploadQueue.filter(q => q.status === 'done').length;
    const failed = uploadQueue.filter(q => q.status === 'error').length;
    const total = succeeded + failed;

    if (failed === 0) {
      toast({
        title: 'Upload Complete',
        description: `Uploaded ${succeeded} of ${total} document${total > 1 ? 's' : ''} successfully.`,
        variant: 'success',
      });
    } else {
      toast({
        title: 'Upload Finished',
        description: `${succeeded} succeeded, ${failed} failed out of ${total} document${total > 1 ? 's' : ''}.`,
        variant: failed === total ? 'destructive' : 'default',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    enqueueFiles(Array.from(files));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    enqueueFiles(files);
  }, [enqueueFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleUploadClick = () => {
    if (onUpload) {
      onUpload();
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleDelete = async (docId: string, docName: string) => {
    try {
      await deleteMutation.mutateAsync(docId);
      toast({
        title: 'Document Deleted',
        description: `${docName} has been deleted.`,
      });
    } catch (error) {
      toast({
        title: 'Delete Failed',
        description: error instanceof Error ? error.message : 'Failed to delete document',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveFromQueue = (id: string) => {
    setUploadQueue(prev => prev.filter(q => q.id !== id));
  };

  return (
    <div
      className="relative flex flex-col h-full w-full"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileUpload}
        accept={ACCEPTED_TYPES}
      />

      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-brand-primary/5 border-2 border-dashed border-brand-primary rounded-lg pointer-events-none">
          <div className="text-center">
            <Upload className="w-12 h-12 mx-auto mb-3 text-brand-primary" />
            <p className="text-lg font-medium text-brand-primary">Drop files to upload</p>
            <p className="text-sm text-brand-primary/70 mt-1">PDF, DOCX, TXT, MD</p>
          </div>
        </div>
      )}

      {/* FIXED HEADER */}
      <div className="shrink-0 bg-white px-6 py-4 border-b border-gray-200">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-medium text-gray-900 tracking-tight">
              Knowledge Base
            </h2>
            <p className="text-base text-gray-600 mt-1">{projectName}</p>
          </div>
          <button
            onClick={handleUploadClick}
            disabled={hasActiveUploads}
            className="h-10 px-4 flex items-center gap-2 bg-brand-primary rounded-[10px] hover:bg-brand-primary/90 transition-colors text-white disabled:opacity-50"
          >
            <Upload className="w-5 h-5" />
            <span className="text-base">
              {hasActiveUploads ? 'Uploading...' : 'Upload Documents'}
            </span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 border border-gray-200 rounded-[10px] p-3">
            <p className="text-base text-gray-600 mb-1">Total Documents</p>
            <p className="text-xl font-medium text-gray-900">{stats.totalDocuments}</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-[10px] p-3">
            <p className="text-base text-gray-600 mb-1">Processed</p>
            <p className="text-xl font-medium text-gray-900">{stats.processed}</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-[10px] p-3">
            <p className="text-base text-gray-600 mb-1">Total Size</p>
            <p className="text-xl font-medium text-gray-900">{stats.totalSize}</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-[10px] p-3">
            <p className="text-base text-gray-600 mb-1">Total Chunks</p>
            <p className="text-xl font-medium text-gray-900">{stats.totalChunks}</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search documents..."
              className="w-full h-[42px] pl-10 pr-4 border border-gray-300 rounded-[10px] text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
            />
          </div>
        </div>
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto bg-gray-50 relative">
        <div className="container">
        <div className="px-6 py-4">
          {/* Upload Queue */}
          {uploadQueue.length > 0 && (
            <div className="mb-4 bg-white border border-gray-200 rounded-[10px] overflow-hidden">
              <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {hasActiveUploads
                    ? `Uploading ${uploadQueue.filter(q => q.status === 'done').length + 1} of ${uploadQueue.length}...`
                    : `Upload complete`}
                </span>
                {allDone && (
                  <button
                    onClick={() => setUploadQueue([])}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Dismiss
                  </button>
                )}
              </div>
              <div className="divide-y divide-gray-100">
                {uploadQueue.map((item) => (
                  <div key={item.id} className="px-4 py-2.5 flex items-center gap-3">
                    <div className="shrink-0">
                      {item.status === 'pending' && <FileText className="w-4 h-4 text-gray-400" />}
                      {item.status === 'uploading' && <Loader2 className="w-4 h-4 text-brand-primary animate-spin" />}
                      {item.status === 'done' && <CheckCircle className="w-4 h-4 text-green-500" />}
                      {item.status === 'error' && <AlertCircle className="w-4 h-4 text-red-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        "text-sm truncate",
                        item.status === 'error' ? 'text-red-600' : 'text-gray-700'
                      )}>
                        {item.file.name}
                      </p>
                      {item.error && (
                        <p className="text-xs text-red-500 truncate">{item.error}</p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">
                      {formatFileSize(item.file.size)}
                    </span>
                    {(item.status === 'pending' || item.status === 'error') && (
                      <button
                        onClick={() => handleRemoveFromQueue(item.id)}
                        className="shrink-0 p-0.5 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Document List or Drop Zone */}
          <div className="flex flex-col gap-3">
            {filteredDocuments.length === 0 ? (
              <div
                onClick={handleUploadClick}
                className={cn(
                  "cursor-pointer text-center py-12 bg-white border-2 border-dashed rounded-[10px] transition-colors",
                  "border-gray-300 hover:border-brand-primary/50 hover:bg-brand-primary/5"
                )}
              >
                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-900 font-medium mb-1">
                  {documents.length === 0 ? 'No documents yet' : 'No documents found'}
                </p>
                <p className="text-gray-500 text-sm mb-4">
                  {documents.length === 0 
                    ? 'Drag and drop files here, or click to browse'
                    : 'Try a different search term'
                  }
                </p>
                {documents.length === 0 && (
                  <p className="text-xs text-gray-400">
                    PDF, DOCX, TXT, MD — up to 50MB per file
                  </p>
                )}
              </div>
            ) : (
              <>
                {filteredDocuments.map((doc) => (
                  <DocumentRow
                    key={doc.id}
                    document={doc}
                    onView={async () => {
                      try {
                        await viewDocument(doc.id);
                      } catch (error) {
                        toast({
                          title: 'View Failed',
                          description: error instanceof Error ? error.message : 'Failed to view document',
                          variant: 'destructive',
                        });
                      }
                    }}
                    onDownload={async () => {
                      try {
                        await downloadDocument(doc.id, doc.name);
                        toast({
                          title: 'Download Started',
                          description: `Downloading ${doc.name}...`,
                        });
                      } catch (error) {
                        toast({
                          title: 'Download Failed',
                          description: error instanceof Error ? error.message : 'Failed to download document',
                          variant: 'destructive',
                        });
                      }
                    }}
                    onDelete={() => handleDelete(doc.id, doc.name)}
                  />
                ))}

                {/* Subtle drop zone below document list */}
                <div
                  onClick={handleUploadClick}
                  className="cursor-pointer text-center py-4 border-2 border-dashed border-gray-200 rounded-[10px] hover:border-brand-primary/40 hover:bg-brand-primary/5 transition-colors"
                >
                  <p className="text-sm text-gray-400">
                    Drop files here or click to upload more
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
